// Modal zum Hinzufügen von Spuren (Tracks) zu einer öffentlichen Playlist.
// Sucht Audio-Produkte (Content), expandiert zu deren Varianten (Tracks) und fügt die gewählte
// Variante hinzu. Ein Playlist-Eintrag referenziert eine KONKRETE Spur (Variante), nicht das Produkt.
import React, { useEffect, useState, useCallback } from 'react';
import { MagnifyingGlassIcon, XMarkIcon, PhotoIcon, PlusIcon, CheckIcon } from '@heroicons/react/24/outline';
import { audioService } from '../../services/audioService';
import { playlistService } from '../../services/playlistService';
import { AudioContent, TrackKindLabels } from '../../types/audio';

interface Track {
  id: string;
  kind: string;
  title?: string | null;
}

// Das Cover einer AUDIO-Zeile steht ausschließlich in `metadata.coverImage` — an der laufenden
// dev-API gemessen (siehe types/audio.ts). Die früheren Fallbacks auf `a.coverImage.url`,
// `a.coverImageUrl` und `a.contentData.coverImage` gab es dort nie; sie waren tote Zweige.
const coverOf = (a: AudioContent): string => a.metadata?.coverImage || '';

interface TrackPickerProps {
  existingTrackIds: Set<string>;
  onAdd: (trackId: string) => Promise<void>;
  onClose: () => void;
}

export const TrackPicker: React.FC<TrackPickerProps> = ({ existingTrackIds, onAdd, onClose }) => {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<AudioContent[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [tracksByContent, setTracksByContent] = useState<Record<string, Track[]>>({});
  const [busyTrackId, setBusyTrackId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (q: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await audioService.getAudioContent({ page: 1, limit: 25, search: q || undefined });
      setResults(res.data || []);
    } catch {
      setResults([]);
      setError('Produkte konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounce der Suche.
  useEffect(() => {
    const t = setTimeout(() => void load(search), 300);
    return () => clearTimeout(t);
  }, [search, load]);

  const toggleExpand = async (content: AudioContent) => {
    if (expandedId === content.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(content.id);
    if (tracksByContent[content.id]) return;
    // Varianten über /api/content/:id laden (dort hängt der ContentService tracks[] an — der
    // Audio-Endpoint /api/audio/:id NICHT). Dedup-Cache pro Content.
    try {
      const tracks = (await playlistService.getContentTracks(content.id)) as Track[];
      setTracksByContent((m) => ({ ...m, [content.id]: tracks }));
    } catch {
      setTracksByContent((m) => ({ ...m, [content.id]: [] }));
    }
  };

  const handleAdd = async (trackId: string) => {
    setBusyTrackId(trackId);
    try {
      await onAdd(trackId);
    } finally {
      setBusyTrackId(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="mt-16 w-full max-w-2xl bg-white rounded-lg shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-secondary-200 px-5 py-4">
          <h3 className="text-lg font-semibold text-secondary-900">Track hinzufügen</h3>
          <button type="button" onClick={onClose} className="text-secondary-400 hover:text-secondary-700">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="p-5">
          <div className="relative mb-4">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary-400" />
            <input
              type="text"
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input pl-10 w-full"
              placeholder="Produkt suchen (Titel)…"
            />
          </div>

          <div className="max-h-[55vh] overflow-y-auto divide-y divide-secondary-100">
            {loading ? (
              <div className="py-10 text-center">
                <div className="loading-spinner mx-auto w-6 h-6"></div>
              </div>
            ) : error ? (
              <p className="py-10 text-center text-error-600">{error}</p>
            ) : results.length === 0 ? (
              <p className="py-10 text-center text-secondary-500">Keine Produkte gefunden</p>
            ) : (
              results.map((content) => {
                const cover = coverOf(content);
                const expanded = expandedId === content.id;
                const tracks = tracksByContent[content.id];
                return (
                  <div key={content.id}>
                    <button
                      type="button"
                      onClick={() => void toggleExpand(content)}
                      className="flex w-full items-center gap-3 py-3 text-left hover:bg-secondary-50"
                    >
                      {cover ? (
                        <img src={cover} alt="" className="h-10 w-10 rounded object-cover" />
                      ) : (
                        <div className="h-10 w-10 rounded bg-secondary-100 flex items-center justify-center">
                          <PhotoIcon className="w-5 h-5 text-secondary-400" />
                        </div>
                      )}
                      <span className="flex-1 text-sm font-medium text-secondary-900">{content.title}</span>
                      <span className="text-xs text-secondary-400">{expanded ? 'Schließen' : 'Varianten'}</span>
                    </button>

                    {expanded && (
                      <div className="pb-3 pl-[52px]">
                        {!tracks ? (
                          <div className="py-3 pl-[52px]"><div className="loading-spinner w-5 h-5"></div></div>
                        ) : tracks.length === 0 ? (
                          <p className="py-2 pl-[52px] text-sm text-secondary-500">Keine spielbaren Varianten</p>
                        ) : (
                          <ul className="space-y-1 pl-[52px]">
                            {tracks.map((tr) => {
                              const already = existingTrackIds.has(tr.id);
                              return (
                                <li
                                  key={tr.id}
                                  className="flex items-center justify-between rounded px-3 py-2 hover:bg-secondary-50"
                                >
                                  <span className="text-sm text-secondary-700">
                                    {TrackKindLabels[tr.kind] || tr.kind}
                                    {tr.title ? ` · ${tr.title}` : ''}
                                  </span>
                                  {already ? (
                                    <span className="inline-flex items-center text-xs text-success-600">
                                      <CheckIcon className="w-4 h-4 mr-1" /> hinzugefügt
                                    </span>
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={() => void handleAdd(tr.id)}
                                      disabled={busyTrackId === tr.id}
                                      className="inline-flex items-center rounded border border-primary-500 px-2.5 py-1 text-xs font-medium text-primary-600 hover:bg-primary-50 disabled:opacity-50"
                                    >
                                      <PlusIcon className="w-4 h-4 mr-1" />
                                      {busyTrackId === tr.id ? '…' : 'Hinzufügen'}
                                    </button>
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
